import { observer } from 'mobx-react-lite';
import { useService } from '~/services/service.base';
import React, { FC, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '~/routes/routes';
import { DialogService } from '~/services/service.dialog';
import { WrapperService } from '~/services/service.wrapper';
import { SignInDialog } from '~/views/organisms/signin.dialog';
import { LinkAccountDialog } from '~/views/organisms/link.account.dialog';
import { RedeemDialog } from '~/views/organisms/redeem.dialog';
import { PrivacyPolicyDialog } from '~/views/organisms/privacy.policy.dialog';
import { UserTopMenu } from '~/views/molecules/user.top.menu';
import { ForbiddenAlertDialog } from '~/views/molecules/forbidden.alert.dialog';

export const PageHeader: FC = observer(() => {
    const dialogService = useService(DialogService);
    const { signInDialogSrv, linkAccountDialogSrv, redeemDialogSrv, privacyPolicyDialogSrv } = dialogService;
    const wrapperService = useService(WrapperService);
    const { authService } = wrapperService;
    const { forbiddenAlertDialogSrv } = dialogService;

    const location = useLocation();
    const isHomePage = location.pathname === ROUTES.MAIN.path;

    const onRedeemBtnClick = useCallback(() => {
        if (!authService.showUnityBanner) {
            redeemDialogSrv.handleOpen(authService);
        } else {
            linkAccountDialogSrv.handleOpen();
        }
    }, [authService, redeemDialogSrv, linkAccountDialogSrv]);

    useEffect(() => {
        if (authService.currentUser && signInDialogSrv.open) {
            signInDialogSrv.handleClose();
        } else if (!authService.loading && !authService.currentUser && !signInDialogSrv.open) {
            signInDialogSrv.setOpen(true);
        }
    }, [authService.currentUser]);

    useEffect(() => {
        forbiddenAlertDialogSrv.setData({
            headerText: 'Something went wrong',
            mainText: 'Looks like we could not log you in to the website, please contact support',
            showContactInfo: true,
        });
    }, []);

    return (
        <header>
            <div className="container header-container">
                <div className={'logo-wrap' + (!isHomePage ? ' logo-wrap-left' : '')}>
                    <a href="/">
                        <img src="/assets/img/logo.svg" />
                    </a>
                </div>
                {authService.currentUser && <UserTopMenu />}
                {!authService.currentUser && (
                    <div className="top-menu-wrap">
                        <div className="sign-in-wrap">
                            <a href="#" onClick={signInDialogSrv.handleOpen} id="sign-in">
                                Sign In
                            </a>
                        </div>
                        <div className="none-user-avatar-wrap">
                            <figure>
                                <img src="/assets/img/none-use-avatar.svg" />
                            </figure>
                        </div>
                    </div>
                )}
            </div>
            {isHomePage && (
                <div
                    className={
                        'container banner-container' + (!authService.currentUser ? ' banner-container-not-logged' : '')
                    }
                    id="banner-container"
                >
                    <div
                        className={
                            'top-unity' +
                            (authService.showBanner && !authService.showUnityBanner ? ' top-unity-linked' : '') +
                            (authService.showBanner && authService.showUnityBanner ? ' top-unity-not-linked' : '')
                        }
                    >
                        {authService.showBanner && authService.showUnityBanner && (
                            <React.Fragment>
                                <div className="unity-logo-wrap">
                                    <figure>
                                        <img className="unity-logo" src="/assets/img/unity-logo.svg" />
                                        <img className="unity-logo-mobile" src="/assets/img/unity-logo-mobile.png" />
                                    </figure>
                                </div>
                                <div className="top-unity-text">
                                    <span>Link or sign up for a Unity Account</span>
                                    <br />
                                    to Redeem your Free Play credits!
                                </div>
                                <div className="top-unity-btn-wrap">
                                    <button
                                        id="link-account-btn"
                                        disabled={!authService.currentUser}
                                        onClick={linkAccountDialogSrv.handleOpen}
                                        type="button"
                                        className="btn"
                                    >
                                        Sign In
                                    </button>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            )}
            {isHomePage && authService.currentUser && authService.showBalance && (
                <div className="container balance-menu-container" id="balance-menu-container">
                    <div className="balance-menu-wrap">
                        <div className="balance-menu">
                            <div className="balance-title">My balance:</div>
                            <div className="total-wrap">
                                <div className="balance-total">
                                    <figure>
                                        <img src="/assets/img/letter-w.svg" />
                                    </figure>
                                    <span className="price-label">Dollars</span>
                                    <span id="balance-total" className="price">
                                        ${authService.currentUser.balance}
                                    </span>
                                </div>
                                <div className="free-play-total">
                                    <figure>
                                        <img src="/assets/img/free-play-icon.svg" />
                                    </figure>
                                    <span className="price-label">Free Play </span>
                                    <span className="price">${authService.currentUser.freePlay}</span>
                                </div>
                            </div>
                        </div>
                        <div className="free-play-btn-wrap">
                            <button id="redeem-btn" type="button" className="btn" onClick={onRedeemBtnClick}>
                                Redeem Free Play
                            </button>
                        </div>
                        <div className="free-play-text"></div>
                    </div>
                </div>
            )}
            {signInDialogSrv.open && !authService.loading ? <SignInDialog /> : null}
            {isHomePage && linkAccountDialogSrv.open ? <LinkAccountDialog /> : null}
            {isHomePage && redeemDialogSrv.open ? <RedeemDialog /> : null}
            {isHomePage && privacyPolicyDialogSrv.open ? <PrivacyPolicyDialog /> : null}
            {authService.showForbiddenPopup && forbiddenAlertDialogSrv.open ? (
                <ForbiddenAlertDialog error={authService.lastError} />
            ) : null}
        </header>
    );
});
